import { validatedDisplayView } from "../../i18n/display-view.js";

/**
 * "Edit" for a built VoiceAgent — customer-portal review #75 (fix round P).
 *
 *   getVoiceAgentAnswers   `GET  /v1/agents/{id}/answers`   a built version's answers, for the form
 *   editVoiceAgent         `POST /v1/agents/{id}/edit`      the next version from edited answers
 *
 * The answers are read back from the version's validated configuration (the wizard's draft does not
 * survive a successful build). An edit opens a NEW version and builds it; the live version keeps
 * answering until that version is published on the Versions tab.
 *
 * A VOICE PER LANGUAGE (plan-015 G1U, gap G1, ADR 0042): the answers carry `voice_by_language` beside the opening
 * language's `voice_id`, and an edit sends the map — never `voice_id`, which the API takes only when a single voice speaks
 * every language.
 *
 * THE ANSWERING ANSWERS (plan-015 AGC-UI, gaps G4, G8, G12; contract IC-DG-2). The answers read back carry the version's
 * own `time_zone`, its `special_date[]` and its choices (`after_hours_choice`, `busy_choice`, `longest_wait_minute`,
 * `forward_to` — `null` together when the version's behaviours are not expressible as them). An edit sends only the
 * group it changes ({@link answeringBodyOf}): a group left out keeps the base version's. `POST /v1/agents` takes the same
 * body from the wizard (`VoiceAgentDraft.answering`, merged below). The new fields are OPTIONAL on the views because the
 * in-memory gateway (`sandbox-gateway.ts`, not this unit's) builds its own literals; this reader always fills them.
 */
import { assertOk, type WeTalkApiClient } from "@wetalk/api-client";

import { readArray, readBoolean, readRecord, readText, readWholeNumber } from "../decode.js";
import { readNullableText } from "../conversation-view.js";
import type { AgentVersionChangeView } from "../gateway.js";
import type {
  AfterHoursChoiceCode,
  AnsweringChoice,
  BusyChoiceCode,
  SpecialDateEntry,
  WaitMinute,
} from "../../routes/agents/new/opening-hours.js";
import type { GatewayGap, SliceContext } from "./slice-context.js";

/** How callers pay, in the wizard's words; `online` since plan-005 OP-38 (the Payments setting). */
export type EditPaymentMethod = "cash_on_delivery" | "card_on_delivery" | "online";

/** One opening period, as the week editor holds it. */
export interface EditOpeningPeriod {
  readonly opensAt: string;
  readonly closesAt: string;
}

export interface VoiceAgentAnswerView {
  readonly voiceAgentId: string;
  readonly agentTemplateCode: string;
  /** The version these answers were read from. */
  readonly agentVersionId: string;
  readonly agentVersionLabel: string;
  readonly liveVersionId: string | null;
  readonly liveVersionSeq: number;
  readonly fieldValue: Readonly<Record<string, string>>;
  /** Per opening-hours field code: seven days, Monday first; an empty day is closed. */
  readonly openingWeek: Readonly<Record<string, readonly (readonly EditOpeningPeriod[])[]>>;
  /** Template text fields the configuration does not carry: asked again. */
  readonly unrecoveredField: readonly string[];
  readonly languageCode: readonly string[];
  /** The opening (greeting) language's voice. */
  readonly voiceId: string;
  /** Language code → its voice, for every language of the version (plan-015 G1). */
  readonly voiceByLanguage: Readonly<Record<string, string>>;
  readonly greeting: string;
  readonly recordingEnabled: boolean;
  readonly paymentMethod: readonly EditPaymentMethod[] | null;
  readonly document: readonly {
    readonly agentDocumentId: string;
    readonly filename: string;
    readonly contentType: string;
    readonly byteSize: number;
  }[];
  /** Why an edit cannot be saved right now (a build is running), or `null`. */
  readonly editRefusal: string | null;
  /** plan-015: the version's own time zone (the workspace's when its document carries no readable hours). */
  readonly timeZone: string;
  /** plan-015: its special dates from today on, in its zone. */
  readonly specialDate: readonly SpecialDateEntry[];
  /** plan-015: its choices; `null` when its behaviours are not expressible as the choices. */
  readonly choice: AnsweringChoice | null;
}

/**
 * The answering answers a request states (plan-015). Each group is optional: on an edit a group left out keeps the base
 * version's; on a create a zone left out is the workspace's and the choices left out are the platform's policy.
 */
export interface AnsweringRequestView {
  readonly timeZone?: string;
  readonly specialDate?: readonly SpecialDateEntry[];
  readonly choice?: AnsweringChoice;
}

export interface VoiceAgentEditRequestView {
  /** The built version the edit starts from; `null` for the live one. */
  readonly agentVersionId: string | null;
  readonly expectedLiveVersionSeq: number;
  readonly fieldValue: Readonly<Record<string, string>>;
  readonly languageCode: readonly string[];
  /** Language code → its voice, one per language in `languageCode` (plan-015 G1). */
  readonly voiceByLanguage: Readonly<Record<string, string>>;
  readonly greeting: string;
  readonly recordingEnabled: boolean;
  /** `null` for a template without an order book: left out of the request. */
  readonly paymentMethod: readonly EditPaymentMethod[] | null;
  readonly documents: "keep" | "replace";
  /** plan-015: the answering groups this edit changes; absent or empty keeps the base version's. */
  readonly answering?: AnsweringRequestView;
}

export interface VoiceAgentEditView {
  readonly agentVersionId: string;
  readonly agentVersionLabel: string;
  readonly buildJobId: string;
  /** `true` when the build is running (documents kept); `false` when it waits for uploads. */
  readonly started: boolean;
  readonly change: readonly AgentVersionChangeView[];
}

declare module "../gateway.js" {
  interface VoiceAgentDraft {
    /** plan-015 AGC-UI: the wizard's answering answers, sent on `POST /v1/agents` by {@link answeringBodyOf}. */
    readonly answering?: AnsweringRequestView;
  }
  interface ConsoleGateway {
    /** #75. A built version's answers (the live one when `agentVersionId` is `null`), for the Edit form. */
    getVoiceAgentAnswers(voiceAgentId: string, agentVersionId: string | null): Promise<VoiceAgentAnswerView>;
    /** #75. Opens the next version from edited answers and builds it; the live version keeps answering. */
    editVoiceAgent(voiceAgentId: string, request: VoiceAgentEditRequestView): Promise<VoiceAgentEditView>;
  }
}

/** Empty: both operations are in the spec. */
export const voiceAgentEditGap: readonly GatewayGap[] = [];

export interface VoiceAgentEditGateway {
  getVoiceAgentAnswers(voiceAgentId: string, agentVersionId: string | null): Promise<VoiceAgentAnswerView>;
  editVoiceAgent(voiceAgentId: string, request: VoiceAgentEditRequestView): Promise<VoiceAgentEditView>;
}

const PAYMENT: readonly EditPaymentMethod[] = ["cash_on_delivery", "card_on_delivery", "online"];
const CHANGE_KIND: readonly AgentVersionChangeView["kind"][] = ["added", "removed", "changed"];

function paymentOfWire(value: unknown, path: string): readonly EditPaymentMethod[] | null {
  if (value === null) return null;
  return readArray(value, path).map((one, index) => {
    const raw = readText(one, `${path}[${String(index)}]`);
    const found = PAYMENT.find((known) => known === raw);
    if (found === undefined) throw new Error(`Unknown payment method "${raw}" at ${path}.`);
    return found;
  });
}

function stringMapOf(value: unknown, path: string): Readonly<Record<string, string>> {
  const record = readRecord(value, path);
  return Object.fromEntries(Object.entries(record).map(([key, one]) => [key, readText(one, `${path}.${key}`)]));
}

const AFTER_HOURS_CODE: readonly AfterHoursChoiceCode[] = ["closed", "message", "forward"];
const BUSY_CODE: readonly BusyChoiceCode[] = ["queue", "message", "forward"];
const WAIT_CODE: readonly WaitMinute[] = [1, 2, 3, 5];

function periodListOfWire(value: unknown, path: string): readonly EditOpeningPeriod[] {
  return readArray(value, path).map((period, at) => {
    const site = `${path}[${String(at)}]`;
    const shaped = readRecord(period, site);
    return { opensAt: readText(shaped["opens_at"], `${site}.opens_at`), closesAt: readText(shaped["closes_at"], `${site}.closes_at`) };
  });
}

/** `SpecialDate[]` on the wire; a `label` left out is the API's "none", `""`. */
export function specialDateListOfWire(value: unknown, path: string): readonly SpecialDateEntry[] {
  return readArray(value, path).map((row, index) => {
    const site = `${path}[${String(index)}]`;
    const shaped = readRecord(row, site);
    return validatedDisplayView({
      date: readText(shaped["date"], `${site}.date`),
      get label() { return shaped["label"] === undefined ? "" : readText(shaped["label"], `${site}.label`); },
      interval: periodListOfWire(shaped["interval"], `${site}.interval`),
    });
  });
}

function waitMinuteOfWire(value: unknown, path: string): WaitMinute {
  const raw = readWholeNumber(value, path);
  const found = WAIT_CODE.find((known) => known === raw);
  if (found === undefined) throw new Error(`Unknown longest wait ${String(raw)} at ${path}.`);
  return found;
}

function codeOf<T extends string>(known: readonly T[], value: unknown, path: string): T {
  const raw = readText(value, path);
  const found = known.find((one) => one === raw);
  if (found === undefined) throw new Error(`Unknown value "${raw}" at ${path}.`);
  return found;
}

/**
 * The four choice fields of a body as one choice set, or `null` when the body says the behaviours are not expressible
 * as the choices (`after_hours_choice` and `busy_choice` both `null`). One `null` without the other raises.
 */
export function answeringChoiceOfWire(one: Readonly<Record<string, unknown>>, path: string): AnsweringChoice | null {
  const after = one["after_hours_choice"];
  const busy = one["busy_choice"];
  if (after === null && busy === null) return null;
  const wait = one["longest_wait_minute"];
  return {
    afterHoursChoice: codeOf(AFTER_HOURS_CODE, after, `${path}.after_hours_choice`),
    busyChoice: codeOf(BUSY_CODE, busy, `${path}.busy_choice`),
    longestWaitMinute: wait === null ? null : waitMinuteOfWire(wait, `${path}.longest_wait_minute`),
    forwardTo: readNullableText(one["forward_to"], `${path}.forward_to`),
  };
}

export function voiceAgentAnswerOfWire(value: unknown, path: string): VoiceAgentAnswerView {
  const one = readRecord(value, path);
  const week = readRecord(one["opening_week"], `${path}.opening_week`);
  return {
    voiceAgentId: readText(one["voice_agent_id"], `${path}.voice_agent_id`),
    agentTemplateCode: readText(one["agent_template_code"], `${path}.agent_template_code`),
    agentVersionId: readText(one["agent_version_id"], `${path}.agent_version_id`),
    agentVersionLabel: readText(one["agent_version_label"], `${path}.agent_version_label`),
    liveVersionId: readNullableText(one["live_version_id"], `${path}.live_version_id`),
    liveVersionSeq: readWholeNumber(one["live_version_seq"], `${path}.live_version_seq`),
    fieldValue: stringMapOf(one["field_value"], `${path}.field_value`),
    openingWeek: Object.fromEntries(
      Object.entries(week).map(([code, days]) => [
        code,
        readArray(days, `${path}.opening_week.${code}`).map((day, index) =>
          readArray(day, `${path}.opening_week.${code}[${index}]`).map((period, at) => {
            const site = `${path}.opening_week.${code}[${index}][${at}]`;
            const shaped = readRecord(period, site);
            return {
              opensAt: readText(shaped["opens_at"], `${site}.opens_at`),
              closesAt: readText(shaped["closes_at"], `${site}.closes_at`),
            };
          }),
        ),
      ]),
    ),
    unrecoveredField: readArray(one["unrecovered_field"], `${path}.unrecovered_field`).map((code, index) =>
      readText(code, `${path}.unrecovered_field[${index}]`),
    ),
    languageCode: readArray(one["language_code"], `${path}.language_code`).map((code, index) =>
      readText(code, `${path}.language_code[${index}]`),
    ),
    voiceId: readText(one["voice_id"], `${path}.voice_id`),
    voiceByLanguage: stringMapOf(one["voice_by_language"], `${path}.voice_by_language`),
    greeting: readText(one["greeting"], `${path}.greeting`),
    recordingEnabled: readBoolean(one["recording_enabled"], `${path}.recording_enabled`),
    paymentMethod: paymentOfWire(one["payment_method"], `${path}.payment_method`),
    document: readArray(one["document"], `${path}.document`).map((row, index) => {
      const site = `${path}.document[${index}]`;
      const shaped = readRecord(row, site);
      return {
        agentDocumentId: readText(shaped["agent_document_id"], `${site}.agent_document_id`),
        filename: readText(shaped["filename"], `${site}.filename`),
        contentType: readText(shaped["content_type"], `${site}.content_type`),
        byteSize: readWholeNumber(shaped["byte_size"], `${site}.byte_size`),
      };
    }),
    editRefusal: readNullableText(one["edit_refusal"], `${path}.edit_refusal`),
    timeZone: readText(one["time_zone"], `${path}.time_zone`),
    specialDate: specialDateListOfWire(one["special_date"], `${path}.special_date`),
    choice: answeringChoiceOfWire(one, path),
  };
}

export function voiceAgentEditOfWire(value: unknown, path: string): VoiceAgentEditView {
  const one = readRecord(value, path);
  return {
    agentVersionId: readText(one["agent_version_id"], `${path}.agent_version_id`),
    agentVersionLabel: readText(one["agent_version_label"], `${path}.agent_version_label`),
    buildJobId: readText(one["build_job_id"], `${path}.build_job_id`),
    started: readBoolean(one["started"], `${path}.started`),
    change: readArray(one["change"], `${path}.change`).map((row, index) => {
      const site = `${path}.change[${index}]`;
      const shaped = readRecord(row, site);
      const raw = readText(shaped["kind"], `${site}.kind`);
      const kind = CHANGE_KIND.find((known) => known === raw);
      if (kind === undefined) throw new Error(`Unknown change kind "${raw}" at ${site}.`);
      return { kind, subject: readText(shaped["subject"], `${site}.subject`), detail: readText(shaped["detail"], `${site}.detail`) };
    }),
  };
}

/** The answering groups as `VoiceAgentCreate` and `VoiceAgentEdit` document them; a group left out is not sent. */
export function answeringBodyOf(answering: AnsweringRequestView | undefined): {
  time_zone?: string;
  special_date?: { date: string; label?: string; interval: { opens_at: string; closes_at: string }[] }[];
  after_hours_choice?: AfterHoursChoiceCode;
  busy_choice?: BusyChoiceCode;
  longest_wait_minute?: WaitMinute | null;
  forward_to?: string | null;
} {
  if (answering === undefined) return {};
  const { timeZone, specialDate, choice } = answering;
  return {
    ...(timeZone === undefined ? {} : { time_zone: timeZone }),
    ...(specialDate === undefined
      ? {}
      : {
          special_date: specialDate.map((one) => ({
            date: one.date,
            ...(one.label === "" ? {} : { label: one.label }),
            interval: one.interval.map((period) => ({ opens_at: period.opensAt, closes_at: period.closesAt })),
          })),
        }),
    ...(choice === undefined
      ? {}
      : {
          after_hours_choice: choice.afterHoursChoice,
          busy_choice: choice.busyChoice,
          longest_wait_minute: choice.longestWaitMinute,
          forward_to: choice.forwardTo,
        }),
  };
}

/** The request body, exactly as `VoiceAgentEdit` documents it. */
export function editBodyOf(request: VoiceAgentEditRequestView): {
  expected_live_version_seq: number;
  agent_version_id?: string;
  field_value: Record<string, string>;
  language_code: string[];
  voice_by_language: Record<string, string>;
  greeting: string;
  recording_enabled: boolean;
  payment_method?: EditPaymentMethod[];
  documents: "keep" | "replace";
} & ReturnType<typeof answeringBodyOf> {
  return {
    expected_live_version_seq: request.expectedLiveVersionSeq,
    ...(request.agentVersionId === null ? {} : { agent_version_id: request.agentVersionId }),
    field_value: { ...request.fieldValue },
    language_code: [...request.languageCode],
    voice_by_language: { ...request.voiceByLanguage },
    greeting: request.greeting,
    recording_enabled: request.recordingEnabled,
    ...(request.paymentMethod === null ? {} : { payment_method: [...request.paymentMethod] }),
    documents: request.documents,
    ...answeringBodyOf(request.answering),
  };
}

export function voiceAgentEditApiSlice(client: WeTalkApiClient, _context: SliceContext): VoiceAgentEditGateway {
  return {
    async getVoiceAgentAnswers(voiceAgentId, agentVersionId): Promise<VoiceAgentAnswerView> {
      const result = await client.getVoiceAgentAnswers({
        path: { voice_agent_id: voiceAgentId },
        query: agentVersionId === null ? {} : { agent_version_id: agentVersionId },
      });
      return voiceAgentAnswerOfWire(readRecord(assertOk(result), "getVoiceAgentAnswers")["data"], "getVoiceAgentAnswers.data");
    },

    async editVoiceAgent(voiceAgentId, request): Promise<VoiceAgentEditView> {
      const result = await client.editVoiceAgent({
        path: { voice_agent_id: voiceAgentId },
        header: { "idempotency-key": crypto.randomUUID() },
        body: editBodyOf(request),
      });
      return voiceAgentEditOfWire(readRecord(assertOk(result), "editVoiceAgent")["data"], "editVoiceAgent.data");
    },
  };
}
